import { useState } from 'react';
import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { Portal } from '../components/Portal';
import { ConfirmModal } from '../taxonomy/ConfirmModal';
import { purgeBeforeIso, todayInputValue } from './purge-date';

/**
 * Contrôle de purge UNIFIÉ, RÉUTILISABLE par le journal des erreurs de fichiers ET
 * le journal des requêtes (Lot C).
 *
 * UN SEUL contrôle : `[ champ date ] [ Purger jusqu'à cette date ]`. Le champ est
 * pré-rempli à la DATE DU JOUR et borné à aujourd'hui (`max`), pas de date future.
 * « jusqu'à cette date » INCLUT le jour choisi → on envoie `before` = minuit du
 * lendemain (voir `purge-date.ts`). Défaut = aujourd'hui ⇒ purge TOUT.
 *
 * Confirmation avant la purge (destructif) via NOTRE modale accessible (jamais
 * `window.confirm`). L'appel réseau réel est délégué au parent (`onPurge(beforeIso)`
 * → nombre supprimé), qui connaît son endpoint et sa bibliothèque.
 */
export interface PurgeByDateControlProps {
  /** Exécute la purge côté API et renvoie le nombre d'entrées supprimées. */
  onPurge: (beforeIso: string) => Promise<number>;
  /** Notifie le parent qu'une purge a réussi (pour recharger sa liste). */
  onPurged?: (() => void) | undefined;
  /** Désactive le contrôle (ex. pas d'accès écriture). */
  disabled?: boolean | undefined;
  /** `now` injectable pour des tests déterministes (défaut : maintenant). */
  now?: Date | undefined;
}

export function PurgeByDateControl({
  onPurge,
  onPurged,
  disabled = false,
  now,
}: PurgeByDateControlProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const today = todayInputValue(now ?? new Date());
  const [date, setDate] = useState(today);
  const [busy, setBusy] = useState(false);
  const [deleted, setDeleted] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Confirmation via NOTRE modale (jamais `window.confirm`). On mémorise le `before`
  // ISO calculé à l'ouverture pour l'exécuter tel quel à la confirmation.
  const [pendingIso, setPendingIso] = useState<string | null>(null);

  // Valide la date et OUVRE la modale de confirmation (rien de destructif ici).
  const run = (): void => {
    if (busy || disabled) return;
    const beforeIso = purgeBeforeIso(date);
    if (beforeIso === null) {
      setError(t('admin.purge.invalidDate'));
      return;
    }
    setError(null);
    setPendingIso(beforeIso);
  };

  // Purge RÉELLE, après confirmation.
  const performPurge = async (): Promise<void> => {
    if (busy || pendingIso === null) return;
    const beforeIso = pendingIso;
    setPendingIso(null);
    setBusy(true);
    setError(null);
    setDeleted(null);
    try {
      const count = await onPurge(beforeIso);
      setDeleted(count);
      onPurged?.();
    } catch {
      setError(t('admin.purge.error'));
    } finally {
      setBusy(false);
    }
  };

  const inputId = 'purge-before-date';
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={inputId} className="text-sm font-medium text-fg">
            {t('admin.purge.dateLabel')}
          </label>
          <input
            id={inputId}
            type="date"
            value={date}
            max={today}
            disabled={disabled || busy}
            onChange={(e) => setDate(e.target.value)}
            className="h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          />
        </div>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => run()}
          className="inline-flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-danger hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
        >
          <TrashIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.purge.button')}
        </button>
      </div>
      {deleted !== null && (
        <p role="status" className="text-sm text-fg">
          {interpolate(t('admin.purge.purged'), { count: deleted }, locale)}
        </p>
      )}
      {error && (
        <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {/* Confirmation (NOTRE modale, jamais `window.confirm`) — portée hors de
          l'en-tête `backdrop-blur`. Action danger : icône + texte. */}
      {pendingIso !== null && (
        <Portal>
          <ConfirmModal
            title={t('admin.purge.title')}
            confirmLabel={t('admin.purge.confirmButton')}
            cancelLabel={t('admin.purge.cancel')}
            busyLabel={t('admin.purge.purging')}
            busy={busy}
            danger
            onConfirm={() => void performPurge()}
            onClose={() => setPendingIso(null)}
          >
            <p>{interpolate(t('admin.purge.confirm'), { date })}</p>
          </ConfirmModal>
        </Portal>
      )}
    </div>
  );
}
